import {
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

import { Link } from 'react-router-dom';
import React from 'react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="text-white bg-gray-900">
      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
          {/* Organization Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4 space-x-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-primary-600 to-primary-800">
                <span className="text-xl font-bold text-white">PBCN</span>
              </div>
              <div>
                <h3 className="text-xl font-bold font-heading">Progressive Black Canadian Network</h3>
                <p className="text-sm text-gray-400">Advocacy • Education • Empowerment</p>
              </div>
            </div>
            <p className="max-w-md mb-6 text-gray-300">
              Empowering Black Canadians through advocacy, education, and community building. 
              We work to address systemic barriers in law, culture, workplace, and beyond.
            </p>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <EnvelopeIcon className="w-5 h-5 text-gray-400" />
                <span className="text-gray-300">info@pbcnetwork.ca</span>
              </div>
              <div className="flex items-center space-x-2">
                <PhoneIcon className="w-5 h-5 text-gray-400" />
                <span className="text-gray-300">+1 (403) 402-5435</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPinIcon className="w-5 h-5 text-gray-400" />
                <span className="text-gray-300">Calgary, Alberta, Canada</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 transition-colors hover:text-primary-400">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 transition-colors hover:text-primary-400">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-300 transition-colors hover:text-primary-400">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 transition-colors hover:text-primary-400">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/donate" className="text-gray-300 transition-colors hover:text-primary-400">
                  Donate
                </Link>
              </li>
            </ul>
          </div>

          {/* Focus Areas */}
          <div>
            <h4 className="mb-4 text-lg font-semibold">Focus Areas</h4>
            <ul className="space-y-2 text-gray-300">
              <li>Legal Advocacy</li>
              <li>Cultural Preservation</li>
              <li>Workplace Equality</li>
              <li>Educational Initiatives</li>
              <li>Community Outreach</li>
              <li>Policy Research</li>
            </ul>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="pt-8 mt-8 border-t border-gray-800">
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="mb-4 text-sm text-gray-400 md:mb-0">
              © {currentYear} Progressive Black Canadian Network. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-primary-400"
                aria-label="Facebook"
                onClick={() => window.open('https://facebook.com/pbcnetwork', '_blank')}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </button>
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-primary-400"
                aria-label="Twitter"
                onClick={() => window.open('https://twitter.com/pbcnetwork', '_blank')}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </button>
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-primary-400"
                aria-label="LinkedIn"
                onClick={() => window.open('https://linkedin.com/company/pbcnetwork', '_blank')}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </button>
              <button
                type="button"
                className="text-gray-400 transition-colors hover:text-primary-400"
                aria-label="Instagram"
                onClick={() => window.open('https://instagram.com/pbcnetwork', '_blank')}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.618 5.367 11.987 11.988 11.987 6.618 0 11.987-5.369 11.987-11.987C24.014 5.367 18.635.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C3.85 14.81 3.29 13.65 3.29 12.353c0-1.297.56-2.458 1.465-3.324C5.621 8.154 6.781 7.593 8.078 7.593c1.297 0 2.448.49 3.323 1.297.875.875 1.435 2.036 1.435 3.333 0 1.297-.49 2.458-1.297 3.324-.875.875-2.036 1.435-3.333 1.435zm7.774-1.435c-.875.875-2.036 1.435-3.333 1.435s-2.458-.56-3.333-1.435c-.875-.875-1.435-2.036-1.435-3.333 0-1.297.56-2.458 1.435-3.333.875-.875 2.036-1.435 3.333-1.435s2.458.56 3.333 1.435c.875.875 1.435 2.036 1.435 3.333 0 1.297-.56 2.458-1.435 3.333z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
